import { z } from 'zod';
import { DOCUMENT_LINE_KINDS, DOCUMENT_STATUSES, DOCUMENT_TYPES, LEDGER_DIRECTIONS, LEDGER_SOURCES, PAYMENT_METHODS, RETURN_REASONS, } from '../constants/index.js';
import { dateOnlySchema, decimalStringSchema, positiveDecimalStringSchema, uuidSchema, } from './common.js';
export const documentLineInputSchema = z
    .object({
    productId: uuidSchema,
    kind: z.enum(DOCUMENT_LINE_KINDS),
    /** Always positive; `kind` carries the direction. */
    quantity: positiveDecimalStringSchema,
    /**
     * Optional: left out, the server uses the product's current list price.
     * Sending a different one requires the price-override setting.
     */
    unitPrice: positiveDecimalStringSchema.optional(),
    returnReason: z.enum(RETURN_REASONS).nullish(),
    notes: z.string().trim().max(500).nullish(),
})
    .refine((line) => line.kind === 'sale' || Boolean(line.returnReason), {
    message: 'documents.errors.returnReasonRequired',
    path: ['returnReason'],
});
export const createDocumentSchema = z.object({
    /**
     * Device-generated, so a retried submission is recognised rather than
     * creating a second document. Required for offline sync in phase 4.
     */
    clientUuid: uuidSchema.optional(),
    type: z.enum(DOCUMENT_TYPES),
    /** Typed in from the paper document; the server does not generate it. */
    number: z.string().trim().min(1).max(40),
    issuedOn: dateOnlySchema,
    customerId: uuidSchema,
    /** Defaults to the vehicle of whoever is creating the document. */
    vehicleId: uuidSchema.nullish(),
    routeId: uuidSchema.nullish(),
    routeStopId: uuidSchema.nullish(),
    lines: z.array(documentLineInputSchema).min(1).max(200),
    notes: z.string().trim().max(2000).nullish(),
});
export const cancelDocumentSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
export const documentLineSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productSku: z.string(),
    productName: z.string(),
    unit: z.string(),
    kind: z.enum(DOCUMENT_LINE_KINDS),
    quantity: z.string(),
    unitPrice: z.string(),
    vatRate: z.number().int(),
    lineNet: z.string(),
    returnReason: z.enum(RETURN_REASONS).nullable(),
    notes: z.string().nullable(),
});
export const vatGroupSchema = z.object({
    vatRate: z.number().int(),
    net: z.string(),
    vat: z.string(),
});
export const documentSchema = z.object({
    id: uuidSchema,
    type: z.enum(DOCUMENT_TYPES),
    status: z.enum(DOCUMENT_STATUSES),
    number: z.string(),
    issuedOn: z.string(),
    customerId: uuidSchema,
    customerCode: z.string(),
    customerName: z.string(),
    createdByUserId: uuidSchema.nullable(),
    createdByUserName: z.string().nullable(),
    vehicleId: uuidSchema.nullable(),
    vehiclePlate: z.string().nullable(),
    routeId: uuidSchema.nullable(),
    netTotal: z.string(),
    vatTotal: z.string(),
    grossTotal: z.string(),
    /** Sum of the payments attached to this document. */
    paidTotal: z.string(),
    notes: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancellationReason: z.string().nullable(),
    createdAt: z.string(),
});
export const documentDetailSchema = documentSchema.extend({
    lines: z.array(documentLineSchema),
    /** One row per VAT rate, as an Italian document requires. */
    vatGroups: z.array(vatGroupSchema),
});
export const documentListQueryExtras = z.object({
    customerId: uuidSchema.optional(),
    type: z.enum(DOCUMENT_TYPES).optional(),
    status: z.enum(DOCUMENT_STATUSES).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    routeId: uuidSchema.optional(),
});
// --- payments ---------------------------------------------------------------
export const createPaymentSchema = z.object({
    clientUuid: uuidSchema.optional(),
    /** Payments are recorded against a document (DECISIONS S3). */
    documentId: uuidSchema,
    amount: positiveDecimalStringSchema,
    method: z.enum(PAYMENT_METHODS),
    paidOn: dateOnlySchema,
    notes: z.string().trim().max(500).nullish(),
});
export const paymentSchema = z.object({
    id: uuidSchema,
    customerId: uuidSchema,
    customerName: z.string(),
    documentId: uuidSchema,
    documentNumber: z.string(),
    amount: z.string(),
    method: z.enum(PAYMENT_METHODS),
    paidOn: z.string(),
    notes: z.string().nullable(),
    createdByUserName: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    createdAt: z.string(),
});
export const paymentListQueryExtras = z.object({
    customerId: uuidSchema.optional(),
    documentId: uuidSchema.optional(),
    method: z.enum(PAYMENT_METHODS).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
// --- ledger -----------------------------------------------------------------
export const ledgerEntrySchema = z.object({
    id: uuidSchema,
    occurredOn: z.string(),
    direction: z.enum(LEDGER_DIRECTIONS),
    amount: z.string(),
    source: z.enum(LEDGER_SOURCES),
    documentId: uuidSchema.nullable(),
    documentNumber: z.string().nullable(),
    documentType: z.enum(DOCUMENT_TYPES).nullable(),
    paymentId: uuidSchema.nullable(),
    reversesEntryId: uuidSchema.nullable(),
    description: z.string().nullable(),
    /** Balance after this entry, in the statement's order. */
    balance: z.string(),
    createdAt: z.string(),
});
/** Everything the customer card shows above the statement. */
export const customerAccountSchema = z.object({
    customerId: uuidSchema,
    customerCode: z.string(),
    customerName: z.string(),
    /** Positive means the customer owes money. */
    balance: z.string(),
    /** Totals over the whole history, not the filtered range. */
    salesTotal: z.string(),
    returnsTotal: z.string(),
    paymentsTotal: z.string(),
    documentCount: z.number().int(),
    lastDocumentOn: z.string().nullable(),
    lastPaymentOn: z.string().nullable(),
});
export const statementQuerySchema = z.object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
/** An opening balance plus the entries in the range, with a running balance. */
export const customerStatementSchema = z.object({
    account: customerAccountSchema,
    /** Balance before the first entry in the range. */
    openingBalance: z.string(),
    entries: z.array(ledgerEntrySchema),
    closingBalance: decimalStringSchema,
});
//# sourceMappingURL=document.js.map